import { useRef, useCallback, useEffect } from 'react';

export interface ProjectFile {
    path: string;
    type: 'file' | 'directory';
    content?: string;
}

/**
 * Hook to manage project files ref for passing to AI context.
 * Files are stored in a ref to avoid re-renders and circular dependencies.
 */
export function useProjectFilesRef() {
    const filesRef = useRef<ProjectFile[]>([]);

    const updateFiles = useCallback((files: Record<string, string>) => {
        // Transform files object to ProjectFile array
        filesRef.current = Object.entries(files).map(([path, content]) => ({
            path,
            type: 'file' as const,
            // Truncate content for large files to save tokens
            content: content.length > 2000 ? content.slice(0, 2000) + '\n// ... truncated' : content,
        }));
    }, []);

    const getFiles = useCallback(() => {
        return filesRef.current;
    }, []);

    const getFilePaths = useCallback(() => {
        return filesRef.current.map(f => f.path);
    }, []);

    return { filesRef, updateFiles, getFiles, getFilePaths };
}
